/**
 * Finding places for a provider's map pin, with Photon (komoot's free OpenStreetMap search:
 * no key, no account; fair use). Search suggests as you type; reverse names the place under
 * a pin that was dragged or dropped.
 */

const PHOTON_URL = "https://photon.komoot.io";

export type Place = {
  latitude: number;
  longitude: number;
  /** The address as one line, such as "1 High Street, BA1 1AA Bath, United Kingdom". */
  address: string;
  /** ISO 3166-1 code, upper case; empty when OpenStreetMap gives none. */
  countryCode: string;
};

type PhotonFeature = {
  geometry?: { coordinates?: [number, number] };
  properties?: Record<string, string | undefined>;
};

/** One line from a Photon result's parts, leaving out what is missing or repeated. */
export function placeFromFeature(feature: PhotonFeature): Place | null {
  const coords = feature.geometry?.coordinates;
  if (!coords || coords.length < 2) return null;
  const p = feature.properties ?? {};
  const street = [p.housenumber, p.street].filter(Boolean).join(" ");
  const town = [p.postcode, p.city ?? p.town ?? p.village ?? p.district].filter(Boolean).join(" ");
  const parts: string[] = [];
  for (const part of [p.name, street, town, p.country]) {
    if (part && !parts.includes(part)) parts.push(part);
  }
  return {
    longitude: coords[0],
    latitude: coords[1],
    address: parts.join(", "),
    countryCode: (p.countrycode ?? "").toUpperCase(),
  };
}

async function photon(path: string, signal?: AbortSignal): Promise<Place[]> {
  const response = await fetch(`${PHOTON_URL}${path}`, { signal });
  if (!response.ok) throw new Error(`Place search failed (${response.status})`);
  const body = (await response.json()) as { features?: PhotonFeature[] };
  return (body.features ?? []).map(placeFromFeature).filter((place): place is Place => place != null);
}

/** Places matching what has been typed, best first. */
export function searchPlaces(query: string, signal?: AbortSignal): Promise<Place[]> {
  return photon(`/api/?q=${encodeURIComponent(query)}&limit=6&lang=en`, signal);
}

/** The place at a point, or null when there is nothing there to name. */
export async function placeAt(latitude: number, longitude: number, signal?: AbortSignal): Promise<Place | null> {
  const places = await photon(`/reverse?lat=${latitude}&lon=${longitude}&lang=en`, signal);
  return places[0] ? { ...places[0], latitude, longitude } : null;
}
